import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Label } from "@/components/ui/label";
import { Mail, ArrowLeft, CheckCircle2, Shield, ArrowRight, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { emailAPI } from "@/services/api";
import laptopBg from "@/components/utils/Logo-login.png";

export function ForgotPasswordForm() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [focusedField, setFocusedField] = useState<boolean>(false);

  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("🔵 [Form] Iniciando proceso de recuperación DIRECTO para:", email);
    setIsLoading(true);
    setError(null);

    if (!email) {
      console.warn("⚠️ [Form] Email vacío");
      toast({
        title: "Campo requerido",
        description: "Por favor ingresa tu dirección de email",
        variant: "destructive",
      });
      setIsLoading(false);
      return;
    }

    try {
      const response = await emailAPI.sendPasswordResetEmail(email);
      console.log("🏁 [Form] Respuesta REAL del servidor:", response);
      setSuccess(true);
      toast({
        title: "Enlace enviado",
        description: "Revisa tu bandeja de entrada para continuar con el proceso",
        variant: "default",
      });
    } catch (err: any) {
      console.error("❌ [Form] Error real:", err);
      setError(err.message || "No se pudo enviar el correo. Intenta nuevamente.");
      toast({
        title: "Error",
        description: err.message || "Error de conexión con el servidor",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  return (
    <div
      className="h-screen w-full relative flex flex-col justify-center overflow-hidden px-4 md:px-8 bg-cover bg-left bg-no-repeat"
      style={{ 
        fontFamily: "'Inter', system-ui, sans-serif",
        backgroundImage: `url(${laptopBg})`,
        backgroundColor: "#1e1b4b" 
      }}
    >
      <div className="w-full max-w-[1300px] mx-auto relative z-10 flex flex-col lg:flex-row items-center justify-end gap-8 lg:gap-12 flex-1 lg:pr-24 xl:pr-48">

        {/* LADO DERECHO: Formulario de recuperación */}
        <div className="w-full max-w-[320px] flex-shrink-0 relative z-30">
           <motion.div
            className="bg-white rounded-3xl p-6 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.5)] border border-slate-100/50 relative"
          >
            {success ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center space-y-6"
              >
                <div className="flex justify-center">
                  <motion.div
                    className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center border border-green-200"
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <CheckCircle2 className="h-8 w-8 text-green-600" />
                  </motion.div>
                </div>

                <div className="space-y-3">
                  <h3 className="text-2xl font-black bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">
                    ¡Enlace enviado!
                  </h3>
                  <p className="text-sm font-medium text-slate-500">
                    Hemos enviado las instrucciones de recuperación a:
                  </p>
                  <div className="font-semibold text-blue-600 bg-blue-50/50 py-2.5 px-5 rounded-2xl inline-block border border-blue-100/50 shadow-sm">
                    {email}
                  </div>
                </div>

                <motion.button
                  onClick={handleBackToLogin}
                  className="w-full py-4 rounded-2xl text-sm font-bold text-white relative overflow-hidden flex items-center justify-center gap-2 shadow-[0_4px_14px_rgba(37,99,235,0.25)]"
                  style={{
                    background: "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <ArrowLeft className="w-5 h-5" />
                  Volver al inicio de sesión
                </motion.button>
              </motion.div>
            ) : (
              <div className="space-y-6">
                {/* Título en degradado limpio */}
                <div className="text-center mb-5">
                  <h1 className="text-xl font-extrabold tracking-tight bg-gradient-to-r from-[#1e1b4b] via-[#312e81] to-[#1e293b] bg-clip-text text-transparent leading-tight">
                    Recuperar contraseña
                  </h1>
                  <p className="text-[12px] font-medium text-slate-500 mt-1 px-2 leading-tight">
                    Te ayudaremos a recuperarla de forma segura
                  </p>
                </div>

                {error && (
                  <motion.div
                    className="flex items-center gap-3 px-4 py-3.5 rounded-2xl border shadow-sm"
                    style={{
                      background: "rgba(254, 242, 242, 0.95)",
                      borderColor: "rgba(252, 165, 165, 0.5)",
                    }}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <AlertCircle className="w-5 h-5 flex-shrink-0 text-red-500" />
                    <p className="text-sm font-semibold text-red-700">{error}</p>
                  </motion.div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Correo Electrónico */}
                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 block">
                      Correo Electrónico
                    </Label>
                    <div
                      className="flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-white/90 backdrop-blur-sm border transition-all duration-200 shadow-[0_4px_14px_rgba(0,0,0,0.02)]"
                      style={{
                        borderColor: focusedField ? "#312e81" : error ? "#fca5a5" : "rgba(226,232,240,0.8)",
                        outline: focusedField ? "2px solid rgba(49,46,129,0.12)" : "none",
                      }}
                    >
                      <Mail className={`w-5 h-5 ${focusedField ? "text-indigo-900" : "text-slate-400"}`} />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onFocus={() => setFocusedField(true)}
                        onBlur={() => setFocusedField(false)}
                        placeholder="ejemplo@empresa.com"
                        className="flex-1 bg-transparent outline-none text-sm font-medium text-slate-800 placeholder-slate-400"
                        required
                        disabled={isLoading}
                        autoComplete="email"
                      />
                    </div>
                  </div>

                  {/* Botón de enviar */}
                  <button
                    type="submit"
                    disabled={isLoading || !email}
                    className="group w-full py-2.5 mt-1 rounded-xl text-[13px] font-bold text-white relative overflow-hidden flex items-center justify-center gap-2 transition-all shadow-[0_4px_14px_rgba(49,46,129,0.25)]"
                    style={{
                      background: (isLoading || !email)
                        ? "#cbd5e1"
                        : "linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)",
                      boxShadow: (isLoading || !email) ? "none" : "0 4px 18px rgba(49,46,129,0.3)",
                      cursor: (isLoading || !email) ? "not-allowed" : "pointer",
                    }}
                  >
                    {isLoading ? (
                      <>
                        <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="white" strokeWidth="4" />
                          <path className="opacity-75" fill="white" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
                        </svg>
                        <span>Enviando...</span>
                      </>
                    ) : (
                      <>
                        <span>Enviar enlace</span>
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}

            {/* Volver al login link */}
            <div className="mt-6 pt-4 border-t border-slate-100 flex flex-col items-center gap-2 text-center">
              <button
                type="button"
                onClick={handleBackToLogin}
                className="inline-flex items-center gap-1.5 text-[12px] font-bold text-indigo-900 hover:text-indigo-950 transition-colors"
                disabled={isLoading}
              >
                <ArrowLeft className="w-3 h-3" />
                Volver al inicio de sesión
              </button>
            </div>

          </motion.div>
        </div>
      </div>

      {/* Footer minimalista con posicionamiento absoluto para no generar scroll */}
      <div className="absolute bottom-6 w-full flex justify-center lg:justify-end lg:pr-24 xl:pr-48 text-[11px] font-semibold text-slate-500 drop-shadow-sm z-10">
        <p>© 2026 Kersales CRM. Todos los derechos reservados.</p>
      </div>
    </div>
  );
}
